import { useMemo, useState, useEffect } from "react";
import styles from "./budgetCalculator.module.css";

const ZAPIER_BUDGET_WEBHOOK = "https://hooks.zapier.com/hooks/catch/17916584/uhdiqkg/";

const FREQS = [
  { key: "weekly", label: "Weekly", toMonthly: (v) => (Number(v) || 0) * 52 / 12 },
  { key: "fortnightly", label: "Fortnightly", toMonthly: (v) => (Number(v) || 0) * 26 / 12 },
  { key: "monthly", label: "Monthly", toMonthly: (v) => Number(v) || 0 },
];

function toMonthly(amount, freqKey) {
  const f = FREQS.find((x) => x.key === freqKey) || FREQS[2];
  return f.toMonthly(amount);
}

// AUD formatter
const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

function Currency({ value }) {
  return <span>{AUD.format(Number(value || 0))}</span>;
}

export default function BudgetCalculator({
  repaymentAmount = 0,
  onClose = () => {},
  onTalkToBroker = () => {},
}) {
  const [step, setStep] = useState(1);

  const [incomes, setIncomes] = useState([
    { id: 1, name: "Applicant 1 salary", amount: "", freq: "monthly" },
    { id: 2, name: "Applicant 2 salary (optional)", amount: "", freq: "monthly" },
    { id: 3, name: "Other income (rent, benefits, etc.)", amount: "", freq: "monthly" },
  ]);

  const [expenses, setExpenses] = useState([
    { id: 1, name: "Groceries", amount: "", freq: "weekly" },
    { id: 2, name: "Utilities (electricity, gas, water, internet)", amount: "", freq: "monthly" },
    { id: 3, name: "Transport", amount: "", freq: "weekly" },
    { id: 4, name: "Insurance", amount: "", freq: "monthly" },
    { id: 5, name: "Childcare / School fees", amount: "", freq: "monthly" },
    { id: 6, name: "Subscriptions", amount: "", freq: "monthly" },
    { id: 7, name: "Other expenses", amount: "", freq: "monthly" },
  ]);

  const [customRepayment, setCustomRepayment] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPageUrl(window.location.href);
    }
  }, []);

  const incomeMonthly = useMemo(
    () => incomes.reduce((sum, r) => sum + toMonthly(r.amount, r.freq), 0),
    [incomes]
  );
  const expensesMonthly = useMemo(
    () => expenses.reduce((sum, r) => sum + toMonthly(r.amount, r.freq), 0),
    [expenses]
  );

  const repaymentMonthly = useMemo(() => {
    const passed = Number(repaymentAmount) || 0;
    const typed = Number(customRepayment) || 0;
    return passed > 0 ? passed : typed;
  }, [repaymentAmount, customRepayment]);

  const leftover = incomeMonthly - expensesMonthly;
  const surplusAfterRepayment = leftover - repaymentMonthly;

  const verdict =
    repaymentMonthly <= 0
      ? { tone: "neutral", text: "Enter an estimated repayment to compare." }
      : surplusAfterRepayment >= 0
        ? { tone: "good", text: "Your budget looks on track to cover the repayments." }
        : { tone: "warn", text: "Your current budget may not comfortably cover these repayments." };

  const addIncome = () =>
    setIncomes((arr) => [...arr, { id: Date.now(), name: "Additional income", amount: "", freq: "monthly" }]);

  const addExpense = () =>
    setExpenses((arr) => [...arr, { id: Date.now(), name: "Additional expense", amount: "", freq: "monthly" }]);

  const updateRow = (setter) => (id, key, value) =>
    setter((arr) => arr.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const removeRow = (setter) => (id) => setter((arr) => arr.filter((r) => r.id !== id));

  async function sendToZap() {
    try {
      setSending(true);

      const params = new URLSearchParams();
      const put = (k, v) => {
        if (v !== undefined && v !== null && String(v).length) {
          params.append(k, String(v));
        }
      };

      // Meta / identifiers
      put("source", "Budget Calculator");
      put("page_url", pageUrl);
      put("submitted_at", new Date().toISOString());
      put("user_agent", typeof navigator !== "undefined" ? navigator.userAgent : "");

      // Totals
      put("income_total_monthly", Math.round(incomeMonthly));
      put("expenses_total_monthly", Math.round(expensesMonthly));
      put("leftover_monthly", Math.round(leftover));
      put("repayment_monthly", Math.round(repaymentMonthly));
      put("surplus_after_repayment", Math.round(surplusAfterRepayment));
      put("repayment_source", (Number(repaymentAmount) || 0) > 0 ? "prop" : "typed");

      // Counts
      put("incomes_count", incomes.length);
      put("expenses_count", expenses.length);

      // Flattened incomes
      incomes.forEach((r, i) => {
        const idx = i + 1;
        const amt = Number(r.amount) || 0;
        const mth = toMonthly(amt, r.freq);
        put(`income_${idx}_name`, r.name);
        put(`income_${idx}_amount_input`, amt);
        put(`income_${idx}_freq`, r.freq);
        put(`income_${idx}_amount_monthly`, Math.round(mth));
      });

      // Flattened expenses
      expenses.forEach((r, i) => {
        const idx = i + 1;
        const amt = Number(r.amount) || 0;
        const mth = toMonthly(amt, r.freq);
        put(`expense_${idx}_name`, r.name);
        put(`expense_${idx}_amount_input`, amt);
        put(`expense_${idx}_freq`, r.freq);
        put(`expense_${idx}_amount_monthly`, Math.round(mth));
      });

      // Send (simple request = no CORS preflight). Zapier will parse to individual fields.
      const res = await fetch(ZAPIER_BUDGET_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: params.toString(),
        keepalive: true,
      });

      if (!res.ok) {
        // Not fatal for UX—Zap usually still records it. Proceed.
        console.warn("Zapier budget webhook non-2xx:", res.status);
      }
    } catch (e) {
      console.warn("Zapier budget submit failed:", e);
    } finally {
      setSending(false);
    }
  }

  async function handleTalkToBroker() {
    // Fire Zap, then continue with your existing action
    await sendToZap();
    onTalkToBroker();
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Budget Calculator">
      <div className={styles.shell}>
        <header className={styles.header}>
          <h2 className={styles.title}>Budget Calculator</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className={styles.steps}>
          <StepBadge active={step === 1}>Income</StepBadge>
          <StepBadge active={step === 2}>Expenses</StepBadge>
          <StepBadge active={step === 3}>Results</StepBadge>
        </div>

        {step === 1 && (
          <section className={styles.panel}>
            <RowsEditor
              rows={incomes}
              onChange={updateRow(setIncomes)}
              onRemove={removeRow(setIncomes)}
              onAdd={addIncome}
              addLabel="Add income"
            />
            <FooterNav onNext={() => setStep(2)} nextLabel="Next: Expenses" />
          </section>
        )}

        {step === 2 && (
          <section className={styles.panel}>
            <RowsEditor
              rows={expenses}
              onChange={updateRow(setExpenses)}
              onRemove={removeRow(setExpenses)}
              onAdd={addExpense}
              addLabel="Add expense"
            />
            <FooterNav onBack={() => setStep(1)} onNext={() => setStep(3)} nextLabel="See Results" />
          </section>
        )}

        {step === 3 && (
          <section className={styles.panel}>
            <div className={styles.summaryGrid}>
              <StatCard label="Total Monthly Income"><Currency value={Math.round(incomeMonthly)} /></StatCard>
              <StatCard label="Total Monthly Expenses"><Currency value={Math.round(expensesMonthly)} /></StatCard>
              <StatCard label="Leftover (Income − Expenses)"><Currency value={Math.round(leftover)} /></StatCard>
              <div className={styles.repaymentBox}>
                <label className={styles.label}>Estimated Monthly Repayment</label>
                {repaymentAmount > 0 ? (
                  <div className={styles.readOnly}><Currency value={Math.round(repaymentMonthly)} /></div>
                ) : (
                  <input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    placeholder="e.g. 2,300"
                    className={styles.input}
                    value={customRepayment}
                    onChange={(e) => setCustomRepayment(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div className={`${styles.verdict} ${styles[verdict.tone]}`}>
              {verdict.text}
              {repaymentMonthly > 0 && (
                <div className={styles.delta}>
                  Surplus after repayment: <strong><Currency value={Math.round(surplusAfterRepayment)} /></strong>
                </div>
              )}
            </div>

            <div className={styles.ctaRow}>
              <button className="mm-btn mm-btn-outline" onClick={() => setStep(2)}>Back</button>
              <button
                className="mm-btn mm-btn-primary"
                onClick={handleTalkToBroker}
                disabled={sending}
                aria-disabled={sending}
                title={sending ? "Sending…" : "Talk to Your Broker"}
              >
                {sending ? "Sending…" : "Talk to Your Broker"}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StepBadge({ active, children }) {
  return <div className={active ? `${styles.badge} ${styles.badgeActive}` : styles.badge}>{children}</div>;
}

function RowsEditor({ rows, onChange, onRemove, onAdd, addLabel }) {
  return (
    <div className={styles.rows}>
      {rows.map((r) => (
        <div key={r.id} className={styles.row}>
          <input
            className={styles.name}
            value={r.name}
            onChange={(e) => onChange(r.id, "name", e.target.value)}
            placeholder="Name"
            aria-label="Item name"
          />
          <input
            className={styles.amount}
            type="number"
            inputMode="decimal"
            min="0"
            placeholder="0"
            value={r.amount}
            onChange={(e) => onChange(r.id, "amount", e.target.value)}
            aria-label="Amount"
          />
          <select
            className={styles.freq}
            value={r.freq}
            onChange={(e) => onChange(r.id, "freq", e.target.value)}
            aria-label="Frequency"
          >
            {FREQS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
          <button className={styles.remove} onClick={() => onRemove(r.id)} aria-label="Remove">✕</button>
        </div>
      ))}
      <button className={styles.add} onClick={addLabel ? onAdd : undefined}>{addLabel || "Add"}</button>
    </div>
  );
}

function StatCard({ label, children }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{children}</div>
    </div>
  );
}

function FooterNav({ onBack, onNext, nextLabel = "Next" }) {
  return (
    <div className={styles.footerNav}>
      {onBack && <button className="mm-btn mm-btn-outline" onClick={onBack}>Back</button>}
      {onNext && <button className="mm-btn mm-btn-primary" onClick={onNext}>{nextLabel}</button>}
    </div>
  );
}
